<script setup lang="ts">
import { ChevronLeftIcon } from "@heroicons/vue/outline";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { RestrictionsFeaturesEnum } from "@/__generated__/types";
import MlTabs from "@/components/molecules/MlTabs/MlTabs.vue";
import useCurrentUser from "@/utils/composables/useCurrentUser/useCurrentUser";
import useRestrictions from "@/utils/composables/useRestrictions/useRestrictions";
import type { Project } from "../types";
import OgProjectHeader from "./OgProjectHeader/OgProjectHeader.vue";
import OgProjectSidebar from "./OgProjectSidebar/OgProjectSidebar.vue";

const CARBON_ACCOUNTING = "carbon_accounting";

type Props = {
	projectId: string;
	projects: Project[];
};

defineProps<Props>();

const isSidePanelHidden = inject("isSidePanelHidden", ref(false));

const route = useRoute();
const { t } = useI18n();
const router = useRouter();
const { isAdminOrSuperAdmin } = useCurrentUser();

const activeTab = computed(() =>
	route.name && !Array.isArray(route.name) ? route.name.toString() : "",
);
const tabs = computed(() => [
	{
		value: "projectsProjectDataEntry",
		name: t("Data Entry"),
		query: route.query,
	},
	...(isAdminOrSuperAdmin.value
		? [
				{
					value: "projectsProjectArchive",
					name: t("Archive"),
					query: route.query,
				},
			]
		: []),
]);

const { restrictions, loading: restrictionsLoading } = useRestrictions();

const esrsPrefixes = [
	"e1",
	"e2",
	"e3",
	"e4",
	"e5",
	"s1",
	"s2",
	"s3",
	"s4",
	"g1",
	"esrs_2",
];

const restricted = computed(() => {
	return (
		(route.query.category === CARBON_ACCOUNTING &&
			restrictions.value?.restrictedFeatures.includes(
				RestrictionsFeaturesEnum.CARBON_ACCOUNTING,
			)) ||
		(esrsPrefixes.some((p) =>
			String(route.query.category).startsWith(`${p}_`),
		) &&
			restrictions.value?.restrictedFeatures.includes(
				RestrictionsFeaturesEnum.ESRS,
			) &&
			!restrictionsLoading.value)
	);
});
</script>

<template>
  <OgProjectSidebar :projectId="projectId" />
  <div class="sticky top-0 bg-white z-10">
    <div class="flex items-center gap-6 px-6 border-gray-200" v-if="!restricted">
      <OgProjectHeader :projectId="projectId" />
      <button
        class="ml-auto text-sm text-nowraprounded mt-2.5 p-1.5 rounded border-gray-200 text-gray-700 border cursor-pointer items-center hover:border-primary hover:text-primary"
        type="button"
        :class="{ hidden: !isSidePanelHidden }"
        @click="isSidePanelHidden = false"
      >
        <ChevronLeftIcon class="w-3 inline-block mb-0.5" />
        {{ t('Open details') }}
      </button>
    </div>
    <div class="border-b flex justify-between mx-6" v-if="!restricted">
      <MlTabs
        :modelValue="activeTab"
        :tabs="tabs"
        isEdgeToEdge
        class="mt-4"
        :hasWideBorderBottom="false"
        @update:modelValue="
          router.push({ name: $event.value, query: $event.query })
        "
      />

      <div id="projectLayoutControls" class="flex items-end" />
    </div>
    <div id="projectSelectActions" />
  </div>
  <router-view ref="pgProjectDataEntry" :projects="projects" v-if="!restricted" />
</template>
